import { translate } from 'i18n/i18n'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from 'utils/unitConverter/types'
import { irrigationActivity } from './irrigationActivity'

// Per hour: 'in' is inches per hour and 'mm' millimeters per hour.
type TIrrigationApplicationRateUnit = 'mm' | 'in'

const DEFAULT_DECIMAL_PLACES = 3

export const irrigationApplicationRate: Record<
  'irrigationApplicationRate',
  (
    rate?: number | null,
    options?: TUnitConverterOptionsWithUnits<TIrrigationApplicationRateUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  irrigationApplicationRate: (
    rate: number | null = null,
    {
      decimalPlaces = DEFAULT_DECIMAL_PLACES,
      inputUnit = 'mm',
      outputUnit = userDetailsStore.getState().rain === 'IMPERIAL' ? 'in' : 'mm',
    }: TUnitConverterOptionsWithUnits<TIrrigationApplicationRateUnit> = {},
  ) => {
    // A rate converts exactly like the depth it applies each hour; only the unit label differs.
    const depthPerHour = irrigationActivity.irrigationActivity(rate, { decimalPlaces, inputUnit, outputUnit })
    const convertedValue = depthPerHour.valueWithNoRounding()

    const measurement =
      outputUnit === 'mm' ? translate.measurements.millimetersPerHour : translate.measurements.inchesPerHour

    const unitSymbol = measurement.unit()
    const label = translate.phrases.banyanApp('Application Rate')

    return {
      categoryTitle: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      categoryTitleWithoutUnit: () => label,
      defaultNumberOfDecimalPlaces: () => DEFAULT_DECIMAL_PLACES,
      suffix: () => unitSymbol,
      shortTitle: () => label,
      title: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      titleWithoutUnit: () => label,
      value: depthPerHour.value,
      valueAsString: () => measurement.value(convertedValue, decimalPlaces),
      valueWithSuffix: () => measurement.valueWithUnit(convertedValue, decimalPlaces),
      valueWithNoRounding: depthPerHour.valueWithNoRounding,
    }
  },
}
